<script setup lang="ts">
/**
 * /resume-synonyms/<slug> — one overused resume word: synonyms grouped by what
 * you actually mean, each group with a before-and-after bullet.
 *
 * The content is one JSON file per word (data/resume-synonyms), imported
 * lazily so a page ships only its own word. The index in
 * constants/resumeSynonyms.ts decides which slugs exist.
 */
import { defaultLocaleSlug } from '~/constants/appLocale'
import { findResumeSynonym, synonymCount, synonymWord, type ResumeSynonymEntry } from '~/constants/resumeSynonyms'

const { t, locale } = useI18n()
const route = useRoute()
const { faqPageNode } = useJsonLd()

definePageMeta({
  layout: false,
  auth: false,
})

// Its URL in Czech, German and Polish (constants/localizedRoutes.ts).
false && /*#__PURE__*/ defineI18nRoute({ paths: { cs: '/synonyma-do-zivotopisu/[slug]', de: '/synonyme-lebenslauf/[slug]', pl: '/synonimy-do-cv/[slug]' } })

// The English slug: under /cs and /de the URL carries a translated one.
const slug = defaultLocaleSlug(route.path)
const indexItem = findResumeSynonym(slug)
if (!indexItem) {
  throw createError({ statusCode: 404, statusMessage: 'Page not found', fatal: true })
}

// English in data/resume-synonyms, Czech and German in data/resume-synonyms/cs|de.
const entries = import.meta.glob<{ default: ResumeSynonymEntry }>('../../data/resume-synonyms/**/*.json')
async function loadEntry(lang: string): Promise<ResumeSynonymEntry> {
  const loader = entries[`../../data/resume-synonyms/${lang === 'cs' || lang === 'de' ? `${lang}/` : ''}${slug}.json`]
  if (!loader) {
    throw createError({ statusCode: 404, statusMessage: 'Page not found', fatal: true })
  }
  return (await loader()).default
}
const entry = shallowRef(await loadEntry(locale.value))
watch(locale, async (lang) => { entry.value = await loadEntry(lang) })

const count = computed(() => synonymCount(entry.value))
const path = `/resume-synonyms/${slug}`
const word = computed(() => synonymWord(indexItem, locale.value))
const lower = computed(() => word.value.toLowerCase())

const seoTitle = computed(() => t('resumeSynonyms.page.seoTitle', { word: word.value, count: count.value }))
const seoDescription = computed(() => t('resumeSynonyms.page.seoDescription', { word: lower.value, count: count.value }))

// Two questions every page answers in its own words: the plain lookup, and
// which one to pick. Both come straight from the entry, so the markup never
// promises more than the page shows.
const faqItems = computed(() => [
  {
    question: t('resumeSynonyms.page.faq.other', { word: lower.value }),
    answer: entry.value.senses.map(sense => `${sense.label}: ${sense.synonyms.map(s => s.word).join(', ')}.`).join(' '),
  },
  {
    question: t('resumeSynonyms.page.faq.use', { word: lower.value }),
    answer: entry.value.tip,
  },
])

const extraNodes = computed(() => [faqPageNode(path, faqItems.value)])
</script>

<template>
  <MarketingFeaturePage
    :seo-title="seoTitle"
    :seo-description="seoDescription"
    :path="path"
    :breadcrumb-title="t('resumeSynonyms.page.heading', { word })"
    :parent="{ name: t('resumeSynonyms.hub.heading'), path: '/resume-synonyms' }"
    :extra-nodes="extraNodes"
  >
    <MarketingResumeSynonymEntry :entry="entry" :word="word" :count="count" />
  </MarketingFeaturePage>
</template>
